/* ==========================================================================
   MATANG 2.0 — PHASE 6b: WEBGL HERO — ART-DIRECTED PASS
   --------------------------------------------------------------------------
   A real Three.js scene (procedural geometry only — no external model
   files) built as a cinematic, art-directed industrial environment: a
   correctly-proportioned ISO shipping container with real door hardware,
   a bolt seal modelled on Matang's actual Container Bolt Seal product
   photography and physically threaded through a hasp on the locking bar,
   palletised export cargo, and a camera that moves between named
   viewpoints on hover — composed so the container reads as the dominant
   object at a deliberate, legible scale rather than an oversized or
   cropped prop.

   Contract with the rest of the storefront (same as matang-3d.js):
   - Everything runs inside a try/catch. A failure here never breaks the
     page — the existing CSS-3D hero (assets/matang-3d.css / matang-3d.js)
     stays in the DOM untouched and simply remains visible.
   - This script does nothing until BOTH: (a) matang-3d.js has classified
     the device as 'full' or 'lite' capability, and (b) WebGL is actually
     available. Reduced-motion, low-power, touch-small-screen and no-JS
     visitors never load or run this — they get the proven CSS-3D hero.
   - Real HTML hotspot links (already in the markup, already crawlable)
     are reused as-is for navigation and for triggering camera moves; this
     script only adds a `.is-webgl-active` class and repositions those
     same anchors to track the 3D objects on screen. No content, no link,
     no accessible name changes.
   ========================================================================== */

(function () {
  'use strict';

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(
        window.WebGLRenderingContext &&
        (c.getContext('webgl') || c.getContext('experimental-webgl'))
      );
    } catch (e) {
      return false;
    }
  }

  function boot() {
    try {
      var root = document.documentElement;
      var quality = root.classList.contains('m3d-full')
        ? 'high'
        : root.classList.contains('m3d-lite')
        ? 'low'
        : null;

      if (!quality || !hasWebGL() || !window.THREE) return;

      var hero = document.querySelector('[data-matang-hero]');
      var mount = document.querySelector('[data-m3d-webgl-hero]');
      if (!hero || !mount || mount.dataset.booted) return;
      mount.dataset.booted = '1';

      var THREE = window.THREE;
      var width = mount.clientWidth || hero.clientWidth || 960;
      var height = mount.clientHeight || 620;

      // A portrait-ish or near-square mount (the stacked mobile/tablet
      // layout in matang-3d.css, or a very tall desktop window) gets its
      // own, tighter composition — "deliberate mobile framing", not the
      // wide establishing shot shrunk down. Decided once at boot from the
      // real box the scene has to fill, not from a hard device-width guess.
      var compact = width / height < 1.15;

      /* ----------------------------------------------------------------
         SCENE / CAMERA / RENDERER
         ---------------------------------------------------------------- */
      var scene = new THREE.Scene();
      scene.fog = new THREE.Fog(0x0a121a, 1100, 3200);

      var camera = new THREE.PerspectiveCamera(compact ? 34 : 32, width / height, 1, 6000);

      var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, quality === 'high' ? 2 : 1.3));
      renderer.setSize(width, height);
      var useShadows = quality === 'high';
      if (useShadows) {
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      }
      if ('outputColorSpace' in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;
      mount.appendChild(renderer.domElement);

      /* ----------------------------------------------------------------
         LIGHTING — a hemisphere fill (cool sky / warm ground bounce)
         replaces a flat ambient so shadow sides never go dead-black, one
         key light with soft shadow, one cool rim light separating the
         container from the background, one warm accent pick-up echoing
         the brand yellow. Fixed, not animated — the realism comes from
         consistent directional shading, not effects.
         ---------------------------------------------------------------- */
      scene.add(new THREE.HemisphereLight(0x8fb4d9, 0x1a1611, 0.65));

      var key = new THREE.DirectionalLight(0xfff2d9, 1.35);
      key.position.set(560, 760, 380);
      if (useShadows) {
        key.castShadow = true;
        key.shadow.mapSize.set(1024, 1024);
        key.shadow.camera.left = -700;
        key.shadow.camera.right = 700;
        key.shadow.camera.top = 700;
        key.shadow.camera.bottom = -700;
        key.shadow.camera.far = 2400;
        key.shadow.bias = -0.0015;
        key.shadow.radius = 3;
      }
      scene.add(key);

      var fill = new THREE.DirectionalLight(0xaecbe8, 0.28);
      fill.position.set(-420, 340, 520);
      scene.add(fill);

      var rim = new THREE.DirectionalLight(0x4fc3ff, 0.32);
      rim.position.set(-680, 300, -460);
      scene.add(rim);

      var accent = new THREE.PointLight(0xf5a623, 0.5, 1100, 2);
      accent.position.set(180, 190, 320);
      scene.add(accent);

      /* ----------------------------------------------------------------
         GROUND — a receiving shadow plane, a faint technical grid, and a
         painted yard bay outline: the same "engineering documentation"
         language as the CSS system rather than a gaming floor or a
         cluttered warehouse.
         ---------------------------------------------------------------- */
      var ground = new THREE.Mesh(
        new THREE.PlaneGeometry(6000, 6000),
        new THREE.MeshStandardMaterial({ color: 0x0b141c, roughness: 0.96, metalness: 0 })
      );
      ground.rotation.x = -Math.PI / 2;
      ground.position.y = -0.5;
      ground.receiveShadow = useShadows;
      scene.add(ground);

      var grid = new THREE.GridHelper(2800, 56, 0x2a3a47, 0x18222c);
      grid.position.y = -0.3;
      grid.material.transparent = true;
      grid.material.opacity = 0.28;
      scene.add(grid);

      // A single painted bay outline under the container + pallet, like a
      // marked parking/loading bay in a real yard — contextual depth
      // without inventing clutter.
      var bayMat = new THREE.MeshBasicMaterial({ color: 0xf5a623, transparent: true, opacity: 0.16 });
      var bayGeoLong = new THREE.PlaneGeometry(900, 4);
      var bayGeoShort = new THREE.PlaneGeometry(4, 300);
      [-150, 150].forEach(function (z) {
        var line = new THREE.Mesh(bayGeoLong, bayMat);
        line.rotation.x = -Math.PI / 2;
        line.position.set(-60, -0.28, z);
        scene.add(line);
      });
      [-390, 480].forEach(function (x) {
        var line = new THREE.Mesh(bayGeoShort, bayMat);
        line.rotation.x = -Math.PI / 2;
        line.position.set(x, -0.28, 0);
        scene.add(line);
      });

      /* ----------------------------------------------------------------
         MATERIALS — matte, physically plausible, no chrome. Two or three
         close-but-different roughness/tint variants per surface family so
         panels read as separately-formed metal rather than one flat hue.
         ---------------------------------------------------------------- */
      var matSteel = new THREE.MeshStandardMaterial({ color: 0x2b3540, roughness: 0.62, metalness: 0.32 });
      var matSteelDim = new THREE.MeshStandardMaterial({ color: 0x1c2530, roughness: 0.68, metalness: 0.28 });
      var matSteelDeep = new THREE.MeshStandardMaterial({ color: 0x11171f, roughness: 0.72, metalness: 0.24 });
      var matFrame = new THREE.MeshStandardMaterial({ color: 0x323d49, roughness: 0.58, metalness: 0.35 });
      var matCasting = new THREE.MeshStandardMaterial({ color: 0x0d1218, roughness: 0.45, metalness: 0.6 });
      var matHardware = new THREE.MeshStandardMaterial({ color: 0xb9c1c8, roughness: 0.42, metalness: 0.75 });
      var matGasket = new THREE.MeshStandardMaterial({ color: 0x08090a, roughness: 0.88, metalness: 0 });
      var matPlacard = new THREE.MeshStandardMaterial({ color: 0xcdd3d6, roughness: 0.5, metalness: 0.1 });
      var matStripe = new THREE.MeshStandardMaterial({ color: 0xf5a623, roughness: 0.5, metalness: 0.05 });

      var matMetal = new THREE.MeshStandardMaterial({ color: 0xcfd6db, roughness: 0.32, metalness: 0.82 });
      var matSealBody = new THREE.MeshStandardMaterial({ color: 0xf3a51f, roughness: 0.55, metalness: 0.08 });
      var matSealNeck = new THREE.MeshStandardMaterial({ color: 0xf3a51f, roughness: 0.6, metalness: 0.05 });

      var matWood = new THREE.MeshStandardMaterial({ color: 0x7d5f3e, roughness: 0.9, metalness: 0 });
      var matWoodDark = new THREE.MeshStandardMaterial({ color: 0x4c3822, roughness: 0.92, metalness: 0 });
      var matCargo = new THREE.MeshStandardMaterial({ color: 0xc9b18d, roughness: 0.88, metalness: 0 });
      var matCargoTape = new THREE.MeshStandardMaterial({ color: 0x8a6a3f, roughness: 0.7, metalness: 0 });
      var matFilm = new THREE.MeshPhysicalMaterial({
        color: 0xdcefff,
        transparent: true,
        opacity: 0.22,
        roughness: 0.14,
        metalness: 0,
        transmission: 0.35,
        depthWrite: false
      });

      /* ----------------------------------------------------------------
         SHIPPING CONTAINER — a 20 ft ISO container's real proportions
         (roughly 2.5 : 1 : 0.94, length : height : depth), built from
         separate structural pieces rather than one box: corrugated long
         sides, top and bottom side rails, 8 corner castings, a raised
         rear door frame, two independently hinged door leaves with their
         own panel ribs, a rubber gasket around the opening, two vertical
         locking bars with cams and keepers, hinge knuckles, a handle, and
         a placard + painted stripe for the "technical labelling" a real
         container carries. Door end faces +X.
         ---------------------------------------------------------------- */
      var W = 520, H = 210, D = 196;
      var container = new THREE.Group();

      var body = new THREE.Mesh(new THREE.BoxGeometry(W, H, D), matSteel);
      body.position.y = H / 2;
      body.castShadow = useShadows;
      body.receiveShadow = useShadows;
      container.add(body);

      // Corrugation — individually placed ridge strips so the panel has
      // real depth under the key light, stopping short of the door end.
      var corrugatedW = W - 34;
      var ridgeGeo = new THREE.BoxGeometry(5, H - 20, 4);
      var ridgeCount = quality === 'high' ? 30 : 16;
      for (var i = 0; i < ridgeCount; i++) {
        var rx = -W / 2 + 10 + i * (corrugatedW / (ridgeCount - 1));
        var ridge = new THREE.Mesh(ridgeGeo, i % 2 === 0 ? matSteel : matSteelDim);
        ridge.position.set(rx, H / 2, D / 2 + 2);
        ridge.castShadow = useShadows;
        container.add(ridge);

        var ridgeBack = ridge.clone();
        ridgeBack.position.z = -D / 2 - 2;
        container.add(ridgeBack);
      }

      // Top and bottom side rails — the structural beams the corrugated
      // panel and corner castings actually hang from on a real container.
      var railGeo = new THREE.BoxGeometry(W - 4, 9, 8);
      [H - 4.5, 4.5].forEach(function (ry) {
        [D / 2 - 3, -D / 2 + 3].forEach(function (rz) {
          var rail = new THREE.Mesh(railGeo, matFrame);
          rail.position.set(0, ry, rz);
          rail.castShadow = useShadows;
          container.add(rail);
        });
      });

      var castGeo = new THREE.BoxGeometry(17, 15, 17);
      [-1, 1].forEach(function (xs) {
        [-1, 1].forEach(function (zs) {
          [7.5, H - 7.5].forEach(function (y) {
            var cast = new THREE.Mesh(castGeo, matCasting);
            cast.position.set(xs * (W / 2 - 8.5), y, zs * (D / 2 - 8.5));
            cast.castShadow = useShadows;
            container.add(cast);
          });
        });
      });

      // Raised door-frame surround at the +X end: reads as a real welded
      // frame the two leaves close against, not just an open edge.
      var frameGroup = new THREE.Group();
      frameGroup.position.set(W / 2 - 2, 0, 0);
      var frameTop = new THREE.Mesh(new THREE.BoxGeometry(6, 10, D + 6), matFrame);
      frameTop.position.set(0, H - 5, 0);
      frameGroup.add(frameTop);
      var frameBottom = new THREE.Mesh(new THREE.BoxGeometry(6, 10, D + 6), matFrame);
      frameBottom.position.set(0, 6, 0);
      frameGroup.add(frameBottom);
      container.add(frameGroup);

      // Rubber gasket — a thin dark loop just inside the frame, the seal
      // strip a real reefer/dry-box door closes against.
      var gasket = new THREE.Mesh(
        new THREE.TorusGeometry(1, 1, 6, 4, Math.PI),
        matGasket
      );
      gasket.rotation.z = Math.PI / 2;
      gasket.rotation.y = Math.PI / 2;
      gasket.scale.set(1, (D - 14) / 2, 1);
      gasket.position.set(W / 2 + 1, H / 2, 0);
      container.add(gasket);

      // Door end faces +X. Each leaf pivots on its own OUTER (away from
      // centre) vertical edge, exactly like a real container door hinge.
      var leafW = D / 2 - 3;
      var leafGeo = new THREE.BoxGeometry(4, H - 16, leafW - 1);
      var ribGeo = new THREE.BoxGeometry(1.6, 4, leafW - 10);

      function buildDoorLeaf(matLeaf) {
        var leaf = new THREE.Group();
        var panel = new THREE.Mesh(leafGeo, matLeaf);
        panel.castShadow = useShadows;
        leaf.add(panel);
        // Two horizontal reinforcement ribs, stamped-panel detail.
        [0.32, 0.68].forEach(function (t) {
          var rib = new THREE.Mesh(ribGeo, matSteelDeep);
          rib.position.set(2.2, (H - 16) * (t - 0.5), 0);
          leaf.add(rib);
        });
        return leaf;
      }

      var doorPivotBack = new THREE.Group(); // hinges at -Z corner
      doorPivotBack.position.set(W / 2, 0, -D / 2);
      var leafBack = buildDoorLeaf(matSteelDim);
      leafBack.position.set(2, H / 2, leafW / 2 + 0.5);
      doorPivotBack.add(leafBack);
      container.add(doorPivotBack);

      var doorPivotFront = new THREE.Group(); // hinges at +Z corner — this
      doorPivotFront.position.set(W / 2, 0, D / 2); // is the leaf the lock-bar
      doorPivotFront.rotation.y = -0.09; // and seal live on, cracked open a
      var leafFront = buildDoorLeaf(matSteelDim); // few degrees on real hinges —
      leafFront.position.set(2, H / 2, -leafW / 2 - 0.5); // a static door reads as inert
      doorPivotFront.add(leafFront); // set-dressing; a barely-open one
      container.add(doorPivotFront); // reads as a door that actually works.

      // Hinge knuckles on both leaves' outer edges.
      var hingeGeo = new THREE.CylinderGeometry(2.6, 2.6, 11, 10);
      [0.2, 0.5, 0.8].forEach(function (t) {
        var hy = H * t - H / 2;
        var hBack = new THREE.Mesh(hingeGeo, matHardware);
        hBack.rotation.x = Math.PI / 2;
        hBack.position.set(4.5, hy, leafW);
        doorPivotBack.add(hBack);
        var hFront = new THREE.Mesh(hingeGeo, matHardware);
        hFront.rotation.x = Math.PI / 2;
        hFront.position.set(4.5, hy, -leafW);
        doorPivotFront.add(hFront);
      });

      // Lock-bar + cam handle, mounted on the inner edge of the front leaf
      // (i.e. at the door centreline where the two leaves meet).
      var lockBar = new THREE.Mesh(new THREE.CylinderGeometry(2.6, 2.6, H - 30, 10), matHardware);
      lockBar.position.set(3, H / 2, -1);
      lockBar.castShadow = useShadows;
      doorPivotFront.add(lockBar);

      [0.14, 0.5, 0.86].forEach(function (t) {
        var keeper = new THREE.Mesh(new THREE.BoxGeometry(9, 9, 9), matSteelDeep);
        keeper.position.set(5, H * t - H / 2 + H / 2, -1);
        doorPivotFront.add(keeper);
      });

      // Locking cams — the angled arms at top and bottom that rotate to
      // engage the door frame's strike plates.
      var camGeo = new THREE.BoxGeometry(13, 2.6, 3);
      [H - 20, 20].forEach(function (cy) {
        var cam = new THREE.Mesh(camGeo, matHardware);
        cam.position.set(9, cy - H / 2, -1);
        cam.rotation.y = 0.5;
        doorPivotFront.add(cam);
      });

      // Handle hardware — a proper D-shaped grab handle, not a single box.
      var handleGroup = new THREE.Group();
      handleGroup.position.set(6, 16 - H / 2, -1);
      var handleBar = new THREE.Mesh(new THREE.CylinderGeometry(1.4, 1.4, 21, 8), matHardware);
      handleBar.rotation.z = Math.PI / 2;
      handleGroup.add(handleBar);
      [-10, 10].forEach(function (hz) {
        var stand = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1.2, 6, 8), matHardware);
        stand.position.set(3, 0, hz);
        handleGroup.add(stand);
      });
      doorPivotFront.add(handleGroup);

      // A door placard (CSC-plate silhouette) and a single painted brand
      // stripe — the "technical labelling" a real container carries,
      // kept abstract rather than inventing fake text.
      var placard = new THREE.Mesh(new THREE.BoxGeometry(0.6, 16, 22), matPlacard);
      placard.position.set(2.6, H * 0.62 - H / 2, leafW * 0.35);
      leafBack.children[0] && leafBack.add(placard);

      // Positioned proud of the corrugation ridges' own outer face (D/2 + 4,
      // since each ridge is 4 deep centred 2 beyond the body panel) rather
      // than tucked just past the body panel itself — a stripe recessed
      // behind the ridge peaks gets sliced into a dashed line wherever a
      // ridge's nearer face wins the depth test, instead of reading as one
      // continuous painted band.
      // PHASE 5B FIX — at the previous length (W - 26 = 494 of the
      // container's 520 units) this stripe spanned nearly the entire body,
      // which reads as a broken/oversized diagonal beam in the WIDE view
      // (visually "bridging" toward the pallet) and competes with the bolt
      // seal for attention in the SECURE close-up. Shortened to a plausible
      // door-end brand band and repositioned toward the door (+x) side,
      // clear of the load group near the far/left end and clear of the
      // door-hardware region (~x 252-258) so it doesn't overlap the handle.
      var stripeLen = 180;
      var stripe = new THREE.Mesh(new THREE.BoxGeometry(stripeLen, 7, 0.6), matStripe);
      stripe.position.set(130, H * 0.58, D / 2 + 4.6);
      container.add(stripe);
      var stripeBack = stripe.clone();
      stripeBack.position.z = -D / 2 - 4.6;
      container.add(stripeBack);

      scene.add(container);

      /* ----------------------------------------------------------------
         CONTAINER BOLT SEAL — modelled on Matang's real Container Bolt
         Seal photography (ME-001 / ME-002): a metal pin with a rounded,
         bullet-nose tip dropping through a welded hasp eye on the
         locking bar into a two-stage moulded body — a domed, wider
         upper barrel stepping down to a narrower base — in the security
         yellow/orange used on the real product. No shackle, no U-bend,
         nothing resembling a padlock anywhere in the silhouette.
         ---------------------------------------------------------------- */
      var seal = new THREE.Group();

      var pinShaft = new THREE.Mesh(new THREE.CylinderGeometry(2.3, 2.3, 21, 12), matMetal);
      pinShaft.position.y = 14;
      pinShaft.castShadow = useShadows;
      seal.add(pinShaft);

      var pinTip = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 2.3, 6.5, 12), matMetal);
      pinTip.position.y = 27.75;
      pinTip.castShadow = useShadows;
      seal.add(pinTip);

      var neck = new THREE.Mesh(new THREE.CylinderGeometry(3.4, 4.6, 5, 14), matSealNeck);
      neck.position.y = 1;
      seal.add(neck);

      var bodyDome = new THREE.Mesh(
        new THREE.SphereGeometry(6.2, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2),
        matSealBody
      );
      bodyDome.position.y = -1.5;
      seal.add(bodyDome);

      var bodyBarrel = new THREE.Mesh(new THREE.CylinderGeometry(6.2, 6.2, 12, 16), matSealBody);
      bodyBarrel.position.y = -6.5;
      bodyBarrel.castShadow = useShadows;
      seal.add(bodyBarrel);

      var bodyShoulder = new THREE.Mesh(new THREE.CylinderGeometry(6.2, 3.6, 4.5, 16), matSealBody);
      bodyShoulder.position.y = -14.75;
      seal.add(bodyShoulder);

      var bodyBase = new THREE.Mesh(new THREE.CylinderGeometry(3.6, 3.6, 5, 14), matSealBody);
      bodyBase.position.y = -19.5;
      seal.add(bodyBase);

      // A single moulded tamper-evident band line near the base — a
      // subtle real-product cue, not a decorative ring.
      var band = new THREE.Mesh(new THREE.TorusGeometry(6.25, 0.4, 6, 20), matSealNeck);
      band.rotation.x = Math.PI / 2;
      band.position.y = -10.5;
      seal.add(band);

      // Hasp eye — a small bracket welded to the lock-bar, angled so its
      // hole reads clearly from the wide view. The pin drops straight
      // through this hole before reaching the body: the physical
      // "installed through the mechanism" relationship the brief calls
      // for, not a seal simply hanging beside the door.
      var hasp = new THREE.Group();
      var haspArm = new THREE.Mesh(new THREE.BoxGeometry(6, 2.6, 2.6), matHardware);
      haspArm.position.set(-3, 0, 0);
      hasp.add(haspArm);
      var haspEye = new THREE.Mesh(new THREE.TorusGeometry(2.8, 0.85, 8, 16), matHardware);
      hasp.add(haspEye);
      // doorPivotFront's own Y axis is already ground-based (0 = floor, H =
      // roof — the same frame leafFront/lockBar/keeper use), so this is a
      // height directly, with no further H/2 re-centring. That extra
      // subtraction was the actual bug in the previous pass: it dropped the
      // hasp and seal ~H/2 below the door entirely, off the lock-bar and
      // invisible below the ground plane — "the most important object"
      // rendering nowhere.
      hasp.position.set(6, H * 0.24, -1);
      doorPivotFront.add(hasp);

      // Seated right through that hasp eye, pin passing down through the
      // ring, body hanging just beneath it — where a real bolt seal is
      // actually installed.
      seal.position.set(6, H * 0.24 - 2, -1);
      doorPivotFront.add(seal);

      /* ----------------------------------------------------------------
         PALLET + CARGO — palletised export cargo to the load's left,
         wrapped in stretch film with two PET strap bands, sitting
         squarely on the pallet deck which itself sits on the ground —
         real contact at every layer, nothing floating.
         ---------------------------------------------------------------- */
      var loadGroup = new THREE.Group();
      loadGroup.position.set(-380, 0, 26);
      scene.add(loadGroup);

      var deck = new THREE.Mesh(new THREE.BoxGeometry(196, 13, 156), matWood);
      deck.position.y = 13;
      deck.castShadow = useShadows;
      deck.receiveShadow = useShadows;
      loadGroup.add(deck);

      var deckSlat = new THREE.BoxGeometry(196, 3, 18);
      [-56, 0, 56].forEach(function (sz) {
        var slat = new THREE.Mesh(deckSlat, matWood);
        slat.position.set(0, 20, sz);
        slat.castShadow = useShadows;
        loadGroup.add(slat);
      });

      var blockGeo = new THREE.BoxGeometry(27, 13, 27);
      [-74, 0, 74].forEach(function (bx) {
        [-58, 58].forEach(function (bz) {
          var block = new THREE.Mesh(blockGeo, matWoodDark);
          block.position.set(bx, 3.5, bz);
          block.castShadow = useShadows;
          loadGroup.add(block);
        });
      });

      var cargoBox = new THREE.Mesh(new THREE.BoxGeometry(130, 76, 108), matCargo);
      cargoBox.position.y = 21.5 + 38;
      cargoBox.castShadow = useShadows;
      cargoBox.receiveShadow = useShadows;
      loadGroup.add(cargoBox);

      // Two tape/seam lines across the cargo box — cardboard-carton cue.
      [-18, 18].forEach(function (ty) {
        var tape = new THREE.Mesh(new THREE.BoxGeometry(1, 76, 2), matCargoTape);
        tape.position.set(ty, 21.5 + 38, 108 / 2 + 0.6);
        loadGroup.add(tape);
      });

      var film = new THREE.Mesh(new THREE.BoxGeometry(138, 84, 116), matFilm);
      film.position.y = 21.5 + 42;
      loadGroup.add(film);

      [-44, 44].forEach(function (sz) {
        var strap = new THREE.Mesh(new THREE.BoxGeometry(142, 6, 6), matStripe);
        strap.position.set(0, 21.5 + 76, sz);
        loadGroup.add(strap);
      });

      /* ----------------------------------------------------------------
         CAMERA WAYPOINTS
         The wide shot is the deliberate establishing composition: a
         three-quarter angle far enough back that the whole container,
         its corner castings, the pallet load and the seal all sit inside
         frame with real margin, the container reading as the dominant
         subject rather than an oversized or cropped prop. Hover-driven
         views then dolly in on one subject at a time. Position and
         look-at are both interpolated every frame for a real dolly move,
         not a cut.
         ---------------------------------------------------------------- */
      // The 'secure' look-at targets the bolt seal's actual world position
      // now that the ground-frame fix above puts it on the lock-bar instead
      // of underground: doorPivotFront sits at (W/2, 0, D/2) with a small
      // -0.09 rad hinge crack, and the seal is offset (6, H*0.24-2, -1) from
      // there — worked through that transform, roughly (266, 46, 96) in
      // world space for this container's W/H/D.
      //
      // PHASE 5B — wide recomposed for the two-column layout. The hero no
      // longer puts the 3D scene full-bleed behind the headline (see the
      // PHASE 6 rewrite in matang-3d.css): copy and scene are back to being
      // two separate, non-overlapping grid columns, so "wide" now only has
      // to fill its own right-hand column, not the full viewport. That
      // column's own aspect ratio varies by viewport (roughly 1.15–1.7),
      // so this position/look/fov was chosen and checked across that whole
      // range rather than against one fixed aspect.
      //
      // The previous wide (pos [555,220,605], look [15,91,-3]) put the
      // camera and the pallet/cargo group on almost the same line of sight
      // through the container: the load group projected to within a few
      // percent of the container's own far corner on screen, at a greater
      // distance, so it read as sitting "behind" the container/headline
      // instead of as its own separate object — the exact bug reported
      // against the live preview. This position pulls the camera further
      // back along Z and drops the look-at target between the container's
      // far end and the load group (rather than near the container's own
      // centre), which separates the two along X instead of stacking them
      // on one ray: container spans roughly the middle-to-right of the
      // frame (far end ~42–45%, door/seal ~78–91%), the pallet/cargo group
      // sits clear to the left of the container's far corner (~32–38%),
      // and there is real margin on every edge at every aspect in range.
      // Depth order reads correctly too: seal/door end nearest the camera,
      // load group and the container's far end both further back and
      // gently caught by the scene fog, giving real atmospheric depth
      // instead of a flat cutout.
      var VIEWS = compact
        ? {
            wide: { pos: [350, 240, 760], look: [-110, 66, 15], fov: 54 },
            secure: { pos: [300, 95, 160], look: [266, 52, 90], fov: 27 },
            pack: { pos: [-800, 320, 680], look: [-380, 55, 30], fov: 32 },
            move: { pos: [-830, 340, 710], look: [-380, 20, 30], fov: 32 }
          }
        : {
            wide: { pos: [300, 220, 1100], look: [-160, 60, 15], fov: 44 },
            secure: { pos: [350, 105, 190], look: [266, 52, 90], fov: 25 },
            pack: { pos: [-750, 300, 650], look: [-380, 55, 30], fov: 30 },
            move: { pos: [-780, 320, 680], look: [-380, 20, 30], fov: 30 }
          };

      var camTarget = new THREE.Vector3(VIEWS.wide.pos[0], VIEWS.wide.pos[1], VIEWS.wide.pos[2]);
      var lookTarget = new THREE.Vector3(VIEWS.wide.look[0], VIEWS.wide.look[1], VIEWS.wide.look[2]);
      var lookCurrent = lookTarget.clone();
      var fovTarget = VIEWS.wide.fov;
      var fovCurrent = fovTarget;
      camera.position.copy(camTarget);
      camera.fov = fovCurrent;
      camera.lookAt(lookCurrent);
      camera.updateProjectionMatrix();

      function goTo(key) {
        var v = VIEWS[key] || VIEWS.wide;
        camTarget.set(v.pos[0], v.pos[1], v.pos[2]);
        lookTarget.set(v.look[0], v.look[1], v.look[2]);
        fovTarget = v.fov || VIEWS.wide.fov;
      }

      /* ----------------------------------------------------------------
         HOTSPOTS — the SAME real anchor elements already in the markup
         (crawlable links to real collections) are reused. On top of the
         existing hover/focus behaviour from matang-3d.js, this script
         also (a) drives the camera to match, and (b) positions each
         anchor once from its stable "home" (wide-camera) projection —
         see PHASE 5C below — rather than tracking the live, moving
         camera, so the control itself never moves while a visitor is
         interacting with it.
         ---------------------------------------------------------------- */
      var hotspotEls = hero.querySelectorAll('.m3d-hotspot');
      var anchors = {
        secure: seal,
        pack: cargoBox,
        move: deck
      };
      var projected = new THREE.Vector3();

      /* ----------------------------------------------------------------
         PHASE 5B — STABLE HOVER REGIONS (fixes the camera "shake")
         ------------------------------------------------------------------
         Root cause of the reported forward/backward/forward shudder: the
         hover hit-test below compared the pointer to each anchor's LIVE
         projected screen position — the same position the camera dolly
         itself moves every frame. As soon as a hover began, the camera
         started animating toward e.g. 'pack', which swept that anchor's
         projected point across the screen as a side effect of the very
         transition it had triggered. Once that moving point drifted far
         enough from a stationary cursor, the hit-test dropped the hover
         and called goTo('wide') — which swept the point back into range,
         re-triggering the hover, forever: a feedback loop between "where
         the camera currently is" and "what the camera should move to
         next", not a duration or easing problem, so shortening the tween
         only made it worse.
         The fix is the "stable invisible interaction region" the brief
         asks for: a second, FIXED projection of each anchor computed once
         from the WIDE camera only (never the live, possibly-mid-transition
         one), used purely for hit-testing. It never moves once computed —
         entering and leaving a hover is judged against a target that
         holds still — while the visible dot/wire/card still track the
         live camera every frame exactly as before, so the visual motion
         during a transition is unchanged. Recomputed on resize only,
         since it depends on aspect ratio, not on anything that changes
         while hovering.
         ---------------------------------------------------------------- */
      var homeCamera = camera.clone();
      var homePositions = {};

      // PHASE 5C FIX — root cause of "the hotspot moves away from the
      // cursor while hovering": updateHotspotPositions() below used to
      // re-project each anchor's WORLD position through the LIVE camera
      // every single frame and write the result straight into the
      // hotspot's el.style.left/top. That live projection is exactly what
      // the camera dolly animates, so the instant a hover began and the
      // camera started moving toward e.g. 'secure', the hotspot's own
      // clickable DOM element swept across the screen right along with
      // it — while hit-testing (below) correctly kept judging hover
      // against the frozen home position, the actual clickable target a
      // visitor sees and has to land the cursor back on had already moved
      // out from under them. Confirmed live: holding the pointer still
      // at the point where a hover began, the hotspot's own
      // getBoundingClientRect() center measurably drifted ~100px away
      // as the camera transitioned.
      //
      // The fix a UI control needs here is exactly what a control should
      // always get: a position in a stable UI/overlay coordinate system,
      // decoupled from whatever the 3D scene underneath is doing. Since a
      // hotspot is only ever meaningful at rest — hovering it is what
      // triggers the camera move toward its object in the first place —
      // its stable "home" position (computed once from the WIDE camera,
      // already used for hit-testing) is also the correct place to paint
      // it, always, regardless of hover state. refreshHomePositions() now
      // writes that fixed position straight into each hotspot element's
      // style directly below, once here and again only on resize; the
      // per-frame live re-projection in updateHotspotPositions() has been
      // removed entirely (see that function) so nothing recalculates or
      // moves the hotspot while a visitor is interacting with it.
      function refreshHomePositions() {
        homeCamera.aspect = camera.aspect;
        homeCamera.fov = VIEWS.wide.fov;
        homeCamera.position.set(VIEWS.wide.pos[0], VIEWS.wide.pos[1], VIEWS.wide.pos[2]);
        homeCamera.up.copy(camera.up);
        homeCamera.lookAt(VIEWS.wide.look[0], VIEWS.wide.look[1], VIEWS.wide.look[2]);
        homeCamera.updateProjectionMatrix();
        homeCamera.updateMatrixWorld(true);

        var home = new THREE.Vector3();
        Object.keys(anchors).forEach(function (key) {
          anchors[key].getWorldPosition(home);
          home.y += key === 'pack' ? 42 : key === 'move' ? 10 : 16;
          var p = home.clone().project(homeCamera);
          var hx = (p.x * 0.5 + 0.5) * width;
          var hy = (-(p.y * 0.5) + 0.5) * height;
          homePositions[key] = { x: hx, y: hy };

          var el = hero.querySelector('.m3d-hotspot[data-focus-key="' + key + '"]');
          if (el) {
            el.style.left = hx + 'px';
            el.style.top = hy + 'px';
          }
        });
      }
      refreshHomePositions();

      // Keyboard access still uses real focus/blur — those track the DOM
      // node itself, not its on-screen position, so they are unaffected by
      // the per-frame repositioning below.
      Array.prototype.forEach.call(hotspotEls, function (el) {
        var key = el.getAttribute('data-focus-key');
        if (!key || !VIEWS[key]) return;
        el.addEventListener('focus', function () { goTo(key); });
        el.addEventListener('blur', function () { goTo('wide'); });
      });

      // Mouse hover deliberately does NOT use mouseenter/mouseleave on the
      // hotspot anchors: PHASE 5C pins each anchor's DOM position, so this
      // is no longer strictly necessary for the anchor itself, but a
      // continuous pointer-vs-home-position comparison is kept anyway —
      // it is what drives the shared hysteresis between 'pack' and 'move'
      // (see HYSTERESIS below), which a plain mouseenter/mouseleave pair
      // per hotspot could not express.
      //
      // The listener is bound to `hero` (the whole hero <section>), not to
      // `mount` (the canvas div) alone: the hotspot anchors — the exact
      // dot/wire/card elements a visitor's cursor lands on — live in a
      // sibling layer stacked above the canvas, not inside it, so a
      // mousemove landing on a hotspot's own DOM never reaches a listener
      // scoped to `mount`. `hero` is a shared ancestor of both, so it
      // receives every hover regardless of which layer the cursor is
      // physically over. Pointer position is still measured against
      // `mount`'s box so it lines up with the anchor projections below,
      // which are computed in that same coordinate space.
      var pointer = { x: null, y: null };
      hero.addEventListener('mousemove', function (e) {
        var r = mount.getBoundingClientRect();
        pointer.x = e.clientX - r.left;
        pointer.y = e.clientY - r.top;
      });
      hero.addEventListener('mouseleave', function () {
        pointer.x = null;
        pointer.y = null;
      });

      // Interaction state machine: IDLE (hoveredKey === null, camera resting
      // at/heading to 'wide') -> HOVER_INTENT/TRANSITIONING (a key just
      // became nearest; goTo() sets new camTarget/lookTarget/fovTarget,
      // animate()'s per-frame lerp carries the camera there) -> ACTIVE
      // (hoveredKey unchanged; the lerp has settled or is settling on that
      // key's view). goTo() is only ever called from the one spot below,
      // and only when nearestKey actually differs from hoveredKey, so an
      // ACTIVE hover never restarts its own transition frame after frame —
      // the earlier "shake" was never really that (no code re-triggered the
      // same transition); it was nearestKey itself flipping rapidly between
      // two different keys because the hit-test target moved. Fixing that
      // input (below) is what makes this state machine behave as a real
      // one: one clean transition in, one clean transition back to wide.
      var hoveredKey = null;
      var HOVER_RADIUS = 34;

      // 'pack' and 'move' sit close together on the pallet/cargo group, and
      // a visitor's cursor is never pixel-perfectly still, so a candidate
      // right at the boundary between two regions (or between a region and
      // empty space) could otherwise flip the decision every frame on
      // motion noise alone. Giving whichever key is ALREADY hovered a
      // fixed head start over any rival candidate means a new key only
      // takes over once it is unambiguously, meaningfully closer.
      var HYSTERESIS = 16;

      function updateHotspotPositions() {
        var nearestKey = null;
        var nearestScore = HOVER_RADIUS;

        // PHASE 5C FIX — no longer re-projects each anchor through the
        // LIVE camera here. That block used to write a fresh
        // el.style.left/top every frame from whatever the camera was
        // doing at that instant, which is what dragged the hotspot's own
        // clickable element out from under a stationary cursor mid-hover
        // (see the long comment above refreshHomePositions). The hotspot's
        // DOM position is now set once from the stable home projection —
        // by refreshHomePositions(), on init and on resize only — so this
        // function's only remaining job is the hit-test itself.
        Object.keys(anchors).forEach(function (key) {
          var home = homePositions[key];
          if (pointer.x !== null && home) {
            var dx = pointer.x - home.x;
            var dy = pointer.y - home.y;
            var dist = Math.sqrt(dx * dx + dy * dy);
            var score = key === hoveredKey ? dist - HYSTERESIS : dist;
            if (score < nearestScore) {
              nearestScore = score;
              nearestKey = key;
            }
          }
        });

        if (nearestKey !== hoveredKey) {
          hoveredKey = nearestKey;
          Array.prototype.forEach.call(hotspotEls, function (el) {
            el.classList.toggle('is-open', el.getAttribute('data-focus-key') === hoveredKey);
          });
          goTo(hoveredKey || 'wide');
        }
      }

      /* ----------------------------------------------------------------
         RENDER LOOP — only while the hero is on screen.
         ---------------------------------------------------------------- */
      var running = true;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          running = entries[0].isIntersecting;
        }, { rootMargin: '150px 0px' }).observe(hero);
      }

      function animate() {
        requestAnimationFrame(animate);
        if (!running) return;
        camera.position.lerp(camTarget, 0.06);
        lookCurrent.lerp(lookTarget, 0.06);
        fovCurrent += (fovTarget - fovCurrent) * 0.06;
        if (Math.abs(fovCurrent - camera.fov) > 0.01) {
          camera.fov = fovCurrent;
          camera.updateProjectionMatrix();
        }
        camera.lookAt(lookCurrent);
        updateHotspotPositions();
        renderer.render(scene, camera);
      }
      animate();

      function onResize() {
        width = mount.clientWidth || width;
        height = mount.clientHeight || height;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
        // The stable hover regions are screen-space and depend on aspect
        // ratio, so they need recomputing whenever the box changes size —
        // but only then, never per-frame (see refreshHomePositions above).
        refreshHomePositions();
      }
      window.addEventListener('resize', onResize, { passive: true });

      // Only now — after the scene has actually built without throwing —
      // do we hide the CSS-3D hero and reveal the canvas. Anything that
      // throws above this line leaves the proven CSS hero fully visible.
      hero.classList.add('is-webgl-active');

      // Adding that class is what applies the WebGL-specific column sizing
      // in matang-3d.css (a taller min-height on the right-hand column) —
      // the mount's real size only exists AFTER this reflow. Re-measuring
      // and resizing now (rather than waiting for a future window resize
      // event) is what stops the renderer opening at the old, smaller box
      // size and then being visibly stretched by CSS to fill the new one.
      onResize();
    } catch (error) {
      if (window.console && console.warn) {
        console.warn('[MatangHero3D] disabled:', error && error.message);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
